import React, { useState } from "react";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { Gift, Sparkles, Check } from "lucide-react";

const PRESET_AMOUNTS = [500, 1000, 2000, 5000];
const HERO_IMG = "https://images.unsplash.com/photo-1513267048331-5611cad62e41?auto=format&fit=crop&w=1920&q=85";

export default function GiftCardsPage() {
  const [amount, setAmount] = useState(1000);
  const [customAmount, setCustomAmount] = useState("");
  const [form, setForm] = useState({
    buyer_name: "", buyer_email: "",
    recipient_name: "", recipient_email: "", message: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const finalAmount = customAmount ? parseFloat(customAmount) : amount;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (finalAmount < 200 || finalAmount > 20000) {
      setErr("El monto debe ser entre $200 y $20,000 MXN, compare");
      return;
    }
    if (!form.buyer_name || !form.buyer_email) {
      setErr("Llena tus datos");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/gift-cards/purchase", {
        amount: finalAmount,
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al procesar");
      setLoading(false);
    }
  };

  return (
    <div className="bg-arre-black min-h-screen" data-testid="gift-cards-page">
      {/* Hero */}
      <section className="relative py-20 border-b border-arre-border overflow-hidden grain">
        <div className="absolute inset-0 bg-cover bg-center opacity-25" style={{ backgroundImage: `url('${HERO_IMG}')` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-arre-black/80 to-arre-black" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-arre-red/15 border border-arre-red/40 text-arre-red text-[10px] tracking-[0.35em] uppercase mb-4">
            <Gift className="w-3 h-3" />
            Tarjetas de regalo
          </div>
          <h1 className="font-display text-5xl md:text-9xl tracking-wider uppercase text-white leading-[0.85]">
            El regalo que<br />
            <span className="text-arre-red italic font-serif-display normal-case tracking-tight">sí se come</span>
          </h1>
          <p className="font-serif-display italic text-xl md:text-2xl text-white/85 mt-6 max-w-2xl">
            Regala la mejor carne 100% sonorense. Tu compare escoge qué cortes, qué paquete y cuándo. Sin expiración, sin fechas.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-10">
          <form onSubmit={submit} className="space-y-8" data-testid="gift-card-form">
            <section>
              <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">Paso 1 · Monto</div>
              <h2 className="font-display text-3xl tracking-wider uppercase text-white mb-4">Elige el valor</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {PRESET_AMOUNTS.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => { setAmount(a); setCustomAmount(""); }}
                    className={`border p-4 transition ${
                      !customAmount && amount === a
                        ? "border-arre-red bg-arre-red/10"
                        : "border-arre-border bg-arre-surface hover:border-white/40"
                    }`}
                    data-testid={`gift-amount-${a}`}
                  >
                    <div className={`font-display text-3xl tracking-wider ${!customAmount && amount === a ? "text-arre-red" : "text-white"}`}>
                      {fmtMXN(a)}
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <label className="block">
                  <span className="text-xs tracking-[0.25em] uppercase text-white/60">O elige monto custom</span>
                  <div className="flex items-center border border-arre-border bg-arre-surface mt-1">
                    <span className="pl-4 text-white/50 font-display text-xl">$</span>
                    <input
                      type="number" min="200" max="20000" step="100"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="Ej. 1500"
                      className="flex-1 bg-transparent text-white font-display text-2xl tracking-wider py-3 px-2 focus:outline-none"
                      data-testid="gift-custom-amount"
                    />
                    <span className="pr-4 text-white/50 text-sm">MXN</span>
                  </div>
                </label>
              </div>
            </section>

            <section>
              <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">Paso 2 · Tus datos</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Tu nombre">
                  <input required value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })}
                    className="w-full bg-arre-surface border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red" data-testid="gift-buyer-name" />
                </Field>
                <Field label="Tu correo (recibes el código)">
                  <input required type="email" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })}
                    className="w-full bg-arre-surface border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red" data-testid="gift-buyer-email" />
                </Field>
              </div>
            </section>

            <section>
              <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">Paso 3 · Destinatario (opcional)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nombre del compare">
                  <input value={form.recipient_name} onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                    placeholder="Opcional" className="w-full bg-arre-surface border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red" />
                </Field>
                <Field label="Correo del compare">
                  <input type="email" value={form.recipient_email} onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                    placeholder="Opcional" className="w-full bg-arre-surface border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red" />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Mensaje personal (opcional)">
                    <textarea rows="3" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="¡Felicidades compare! Te mereces un buen asado."
                      className="w-full bg-arre-surface border border-arre-border px-4 py-3 text-white focus:outline-none focus:border-arre-red resize-none" />
                  </Field>
                </div>
              </div>
            </section>

            {err && <div className="p-3 border border-arre-red bg-arre-red/10 text-sm text-white">{err}</div>}
          </form>

          <aside className="lg:sticky lg:top-28 self-start">
            <div className="bg-arre-surface border border-arre-border">
              <div className="p-5 border-b border-arre-border">
                <div className="text-xs tracking-widest uppercase text-white/50">Resumen</div>
                <div className="font-display text-xl tracking-wider uppercase text-white mt-1">Tarjeta de regalo</div>
              </div>
              <div className="p-5">
                <div className="text-xs tracking-widest uppercase text-white/50">Monto</div>
                <div className="font-display text-5xl text-white mt-1" data-testid="gift-summary-amount">{fmtMXN(finalAmount || 0)}</div>
                <ul className="mt-5 space-y-2 text-sm text-white/70">
                  <li className="flex gap-2"><Check className="w-4 h-4 text-arre-red flex-shrink-0 mt-0.5" /> Se redime con código único</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-arre-red flex-shrink-0 mt-0.5" /> Sin fecha de expiración</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-arre-red flex-shrink-0 mt-0.5" /> Combinable con cupones</li>
                  <li className="flex gap-2"><Check className="w-4 h-4 text-arre-red flex-shrink-0 mt-0.5" /> Saldo restante queda en la tarjeta</li>
                </ul>
                <button
                  form="" onClick={submit}
                  disabled={loading || finalAmount < 200}
                  className="btn-arre w-full bg-arre-red hover:bg-arre-red-dark text-white font-display text-lg tracking-widest uppercase py-4 mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="gift-submit"
                >
                  {loading ? "Procesando..." : `Comprar tarjeta ${fmtMXN(finalAmount || 0)} →`}
                </button>
                <div className="flex items-center gap-2 text-[11px] text-white/40 mt-3 justify-center">
                  <Sparkles className="w-3 h-3" />
                  Pago seguro con Stripe
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.25em] uppercase text-white/60">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
